<template lang="pug">
#game
  #game-area
    #press-enter(v-if="isWait()")
      span#press-enter-to-start {{ $t("game.press-enter-to-start-text") }}
      p#time-limit {{ $t("game.time-limit-text") }}
    #start-countdown(v-if="isStart()")
      span#start-countdown-sec.start-countdown {{ countDownToStart }}
    #in-game-area(v-if="isPlaying()")
      #game-time-area
        p#game-time {{ $t("game.time-remaining") }}: {{ remainingGameTimeStr }}
      #question-area
        #question-text.text-area(v-if="isJP()")
          p {{ textData.text }}
        #hiragana-text-area.text-area(v-if="isJP()")
          p#hiragana （{{ textData.hiragana }}）
        #roman-text-area.text-area
          span.roman-letter(:id="'roman-letter-' + index" v-for="(letter, index) in textData.roman") {{ letter }}
    #game-end-area(v-if="isProccessingEnd()")
      p#game-end-text TIME OVER
      span#press-enter-to-result {{ $t("game.press-enter-to-go-to-result") }}
</template>

<script>

import { mapState, mapActions } from 'vuex'
import KEY_CODE_MATRIX from '@/common/key_code_matrix'
import GAME_STATE from '@/common/game_state'
import TEXT_DATA from '@/common/text'

const COUNT_DOWN_SECS = 3
const GAME_TIME_SECS = 60

export default {
  created: function () {
    this.setStatus(GAME_STATE.WAIT)
    this.typeCount = 0
    this.correctLetterCount = 0
    this.clearQuestionCount = 0
    this.remainingGameTime = GAME_TIME_SECS
    this.initialize()
    window.addEventListener('keydown', this.detectKeyAction)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.detectKeyAction)
  },
  computed: {
    ...mapState({
      gameState: state => state.Game
    }),
    status: function () {
      return this.gameState.status
    },
    textData: function () {
      if (!this.gameState) {
        return
      }
      return this.gameState.textData
    },
    textDataCount: function () {
      return this.gameState.textDataCount
    },
    currentLetterIndex: function () {
      return this.gameState.currentLetterIndex
    },
    currentCorrectLetter: function () {
      return this.gameState.currentCorrectLetter
    },
    currentTextLastIndex: function () {
      return this.gameState.currentTextLastIndex
    },
    currentTextDataIndex: function () {
      return this.gameState.currentTextDataIndex
    },
    wrongLetterData: function () {
      return this.gameState.wrongLetterData
    }
  },
  methods: {
    ...mapActions({
      setGameStatus: 'Game/setStatus',
      setGameCurrentLetterIndex: 'Game/setCurrentLetterIndex',
      setGameCurrentTextDataIndex: 'Game/setCurrentTextDataIndex',
      addGameWrongLetter: 'Game/addWrongLetter',
      initializeGameResult: 'Result/initialize'
    }),
    isJP: function () {
      return this.$i18n.locale === 'ja'
    },
    initializeResult: function () {
      const result = {
        typeCount: this.typeCount,
        wrongLetterData: this.wrongLetterData,
        clearQuestionCount: this.clearQuestionCount,
        correctLetterCount: this.correctLetterCount
      }
      this.initializeGameResult(result)
    },
    isWait: function () {
      return this.status === GAME_STATE.WAIT
    },
    isStart: function () {
      return this.status === GAME_STATE.START
    },
    isPlaying: function () {
      return this.status === GAME_STATE.PLAYING
    },
    isProccessingEnd: function () {
      return this.status === GAME_STATE.END
    },
    initialize: function (index = 0) {
      this.setCurrentTextDataIndex(index)
      this.setCurrentLetterIndex(0)
      const romanElements = document.getElementsByClassName('roman-letter')
      Array.from(romanElements).forEach((elem) => {
        elem.classList.remove('letter-answered-correctly')
      })
    },
    initializeNextQuestion: function () {
      this.incrementClearQuestionCount()
      let nextTextDataIndex = 0
      if (this.clearQuestionCount < 3) {
        nextTextDataIndex = this.currentTextDataIndex + 1
      } else {
        nextTextDataIndex = Math.floor(Math.random() * (TEXT_DATA[this.$i18n.locale].LIST.length))
      }
      this.initialize(nextTextDataIndex)
    },
    addWrongLetter: function (letter) {
      this.addGameWrongLetter({ letter: letter })
    },
    setStatus: function (status) {
      this.setGameStatus({ status: status, locale: this.$i18n.locale })
    },
    setCurrentLetterIndex: function (index) {
      this.setGameCurrentLetterIndex({ letterIndex: index })
    },
    setCurrentTextDataIndex (index) {
      this.setGameCurrentTextDataIndex({ textDataIndex: index, locale: this.$i18n.locale })
    },
    isQuestionDataExist: function () {
      return this.textData
    },
    isLastQuestion: function () {
      return (this.textDataCount - 1) === this.currentTextDataIndex
    },
    goToResult: function () {
      this.initializeResult()
      this.$router.push({ path: 'result' })
    },
    executeLetterAnimation: function () {
      const elementId = `roman-letter-${this.currentLetterIndex}`
      const letterElement = document.getElementById(elementId)
      if (!letterElement) {
        return
      }
      letterElement.classList.add('letter-answered-correctly')
    },
    isLastLetter: function () {
      return this.currentLetterIndex === this.currentTextLastIndex
    },
    isCorrectLetter: function (inputLetter) {
      return inputLetter === this.currentCorrectLetter
    },
    gameStartCountDown: function () {
      this.countDownToStart = COUNT_DOWN_SECS
      this.setIntervalId = setInterval(function () { this.countDown() }.bind(this), 1000)
    },
    remainingGameTimeCountDown: function () {
      this.remainingGameTime = (this.remainingGameTime - 0.1).toFixed(1)
      if (this.remainingGameTime <= 0) {
        this.remainingGameTimeStr = 0
        clearInterval(this.setIntervalId)
        this.processGameEnd()
        return
      }
      this.remainingGameTimeStr = this.remainingGameTime
    },
    startGame: function () {
      this.setStatus(GAME_STATE.PLAYING)
      this.setIntervalId = setInterval(function () { this.remainingGameTimeCountDown() }.bind(this), 100)
    },
    countDown: function () {
      const newCount = this.countDownToStart - 1
      if (newCount <= 0) {
        this.countDownToStart = ''
        clearInterval(this.setIntervalId)
        this.startGame()
        return
      }
      const countDownElements = document.getElementById('start-countdown-sec')
      countDownElements.classList.remove('start-countdown')
      countDownElements.classList.add('start-countdown')
      this.countDownToStart = newCount
    },
    processGameEnd: function () {
      this.setStatus(GAME_STATE.END)
    },
    incrementTypeCount: function () {
      this.typeCount += 1
    },
    incrementClearQuestionCount: function () {
      this.clearQuestionCount += 1
    },
    incrementCorrectLetterCount: function () {
      this.correctLetterCount += 1
    },
    detectKeyAction: function (e) {
      if (this.isWait()) {
        if (e.keyCode === 13) {
          this.setStatus(GAME_STATE.START)
          this.gameStartCountDown()
        }
        return
      }

      if (this.isProccessingEnd()) {
        if (e.keyCode === 13) {
          this.goToResult()
        }
        return
      }

      this.incrementTypeCount()

      const res = KEY_CODE_MATRIX.CODE_MATRIX.find((matrix) => {
        return e.keyCode === matrix.code
      })

      if (!res) {
        return
      }

      if (!this.isCorrectLetter(res.alphabet)) {
        // 間違い
        this.addWrongLetter(this.currentCorrectLetter)
        return
      }

      this.incrementCorrectLetterCount()

      this.executeLetterAnimation()
      if (this.isLastLetter()) {
        this.initializeNextQuestion()
        return
      }
      this.setCurrentLetterIndex(this.currentLetterIndex + 1)
    }
  },
  data () {
    return {
      database: null,
      countDownToStart: 0,
      setIntervalId: null,
      remainingGameTime: 0,
      remainingGameTimeStr: '',
      typeCount: 0,
      clearQuestionCount: 0,
      correctLetterCount: 0
    }
  }
}

</script>

<style>
#game {
  margin: 0 auto;
}

#press-enter {
  margin-top: 150px;
}

#press-enter-to-start {
  font-size: 50px;
  animation: press-enter-to-start-animation 1s linear infinite;
}

#time-limit {
  margin-top: 100px;
}

#in-game-area {
  width: 100%;
  height: 100%;
}

#question-area {
  font-size: 50px;
}

#game-time-area {
  text-align: right;
}

#roman-text-area {
  color: gray;
  min-width: 400px;
  letter-spacing: 10px;
}

.letter-answered-correctly {
  color: red;
  transform: rotateY(0deg);
  animation: correct-letter-animation 0.5s linear;
}

#game-end-text {
  margin-top: 200px;
  animation: game-end-text-timeover-animation 1s linear;
  font-size: 100px;
}

#press-enter-to-result {
  animation: press-enter-to-start-animation 1s linear infinite;
}

@keyframes correct-letter-animation {
  0% {
    font-size: 50px;
  }
  50% {
    font-size: 58px;
  }
  100% {
    font-size: 50px;
  }
}

#start-countdown-sec {
  font-size: 200px;
}

.start-countdown {
  animation: start-countdown-animation 1s linear infinite;
}

@keyframes start-countdown-animation {
  0% {
    font-size: 200px;
  }
  100% {
    font-size: 300px;
    opacity: 0;
  }
}

@keyframes press-enter-to-start-animation {
  0% {
    opacity: 1.0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1.0;
  }
}

@keyframes game-end-text-timeover-animation {
  from {
    opacity: 0;
    transform: rotateY(0deg);
  }
  to {
    opacity: 1;
    transform: rotateY(-360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Game.vue